import { useContext } from 'react'

import { SHOPIFY, WOOCOMERCE, VTEX } from '../../constants/cms'
import { SHOPIFY_PINFLAG_PAYMENT_METHOD } from '../../constants/payment'
import { DataContext } from '../../context/DataContext'
import { pinflagPaymentSuccessEvent } from '../../helpers/eventsHelpers'
import {
  createCustomAttributesForShopify,
  parseOrderShopify,
  parseOrderWoocommerce,
  parseOrderVtex
} from '../../helpers/payment/purchaseOrderParsers'
import {
  createPurcharseOrderRequest,
  markShopifyOrderAsPaidRequest,
  refundPaymentOrderRequest
} from '../../helpers/pinflagRequest'
import { useSteps } from '../useSteps'
import { useVtex } from '../useVtex'

import { usePayment } from './usePayment/usePayment'

export const USE_SCRAPER_FOR_SHOPIFY = false

export const usePurchaseOrder = () => {
  const {
    cartData,
    setShowFinalLoader,
    cartNotes,
    isSaving,
    companyInfo,
    extraAttributesShopify,
    billingInfo,
    userInfo,
    discountInfo,
    ecommerceInfo,
    selectedServiceType
  } = useContext(DataContext)
  const { cms, vtexOrderFormId } = ecommerceInfo
  const { useTwoClick } = companyInfo
  const {
    purchaseOrderData,
    setSelectedNativePaymentMethod,
    paymentState,
    setPaymentState,
    setPurchaseOrderResult,
    resetCardData
  } = usePayment()

  const { setShippingInfoVtex, saveUserInformationVtex } = useVtex()

  const { goToPurchaseOrderConfirmation } = useSteps()
  const { discountCodes } = discountInfo

  const createPurchaseOrderWoocommerce = async (data) => {
    const parsedOrderData = parseOrderWoocommerce(data)
    const response = await createPurcharseOrderRequest(parsedOrderData)
    return response
  }

  const createPurchaseOrderVtex = async (data, vtexOrderFormId) => {
    await saveUserInformationVtex(userInfo, billingInfo)
    await setShippingInfoVtex()
    const parsedOrderData = parseOrderVtex(data, vtexOrderFormId)
    const response = await createPurcharseOrderRequest(parsedOrderData)
    return response
  }

  const createPendingPurchaseOrderShopify = () => {
    // showLoaderInfo is already true and we are in the payment section
    // so if we set the native payment method here this will trigger
    // the scrapper to finish the order with the method 'Pinflag'
    setSelectedNativePaymentMethod(SHOPIFY_PINFLAG_PAYMENT_METHOD)
    return { isUsingScraper: true }
  }

  const createPurchaseOrderShopify = async (purchaseOrderData) => {
    const parsedOrderData = parseOrderShopify(purchaseOrderData)
    return await createPurcharseOrderRequest(parsedOrderData)
  }

  const handleCreatePurchaseOrderShopify = async (purchaseOrderData) => {
    if (USE_SCRAPER_FOR_SHOPIFY) {
      return createPendingPurchaseOrderShopify()
    } else {
      return await createPurchaseOrderShopify(purchaseOrderData)
    }
  }

  const createPurchaseOrder = (
    { gateway, paymentMethod, referenceId } = {
      gateway: paymentState.gateway,
      paymentMethod: paymentState.selectedPaymentOption
    }
  ) => {
    const { ecommerce } = purchaseOrderData
    const { cms: preFormattedCms, siteCookies } = ecommerce
    const customAttributes = preFormattedCms === SHOPIFY
      ? createCustomAttributesForShopify({
        siteCookies,
        useTwoClick,
        isSaving,
        extraAttributesShopify,
        billingInfo,
        userInfo
      })
      : {}
    const orderData = {
      ...purchaseOrderData,
      gateway,
      cartNotes,
      selectedServiceType,
      paymentMethod,
      customAttributes,
      discountCodes,
      referenceId
    }

    const cms = preFormattedCms.includes('woocommerce')
      ? WOOCOMERCE
      : preFormattedCms
    switch (cms) {
      case SHOPIFY:
        return handleCreatePurchaseOrderShopify(orderData)
      case WOOCOMERCE:
        return createPurchaseOrderWoocommerce(orderData)
      case VTEX:
        return createPurchaseOrderVtex(orderData, vtexOrderFormId)
      default:
        return null
    }
  }

  const markShopifyOrderAsPaid = async () => {
    const response = await markShopifyOrderAsPaidRequest({ cart: cartData })
    setPaymentState((prevState) => ({
      ...prevState,
      paymentSuccessfulInformation: {
        ...prevState.paymentSuccessfulInformation,
        purchaseOrderId: response.name.replace('#', '')
      }
    }))
    setPurchaseOrderResult(response)
    pinflagPaymentSuccessEvent(SHOPIFY)
    goToPurchaseOrderConfirmation()
    setShowFinalLoader(false)
  }

  const handlePurchaseOrderSuccess = async (response) => {
    setPaymentState((prevState) => ({
      ...prevState,
      paymentSuccessfulInformation: {
        ...prevState.paymentSuccessfulInformation,
        purchaseOrderId: response.orderId
      }
    }))
    setPurchaseOrderResult(response)
    pinflagPaymentSuccessEvent(cms)
    goToPurchaseOrderConfirmation()
    setShowFinalLoader(false)
  }

  const refundPaymentOrder = async ({
    paymentGateway,
    orderId,
    referenceId
  }) => {
    const body = {
      paymentGateway,
      refundData: {
        orderId,
        referenceId,
        amount: cartData.total,
        type: 'complete'
      }
    }
    await refundPaymentOrderRequest(body)
  }

  const retryPurchaseOrder = async ({
    paymentGateway,
    paymentMethod,
    orderId,
    referenceId,
    retries
  }) => {
    if (retries > 3) {
      return handlePurchaseOrderError({ paymentGateway, orderId, referenceId })
    }
    try {
      const response = await createPurchaseOrder({
        gateway: paymentGateway,
        paymentMethod,
        referenceId
      })
      return response
    } catch (error) {
      console.error('❗Error al crear la orden de compra:', error)
      return await retryPurchaseOrder({
        paymentGateway,
        paymentMethod,
        orderId,
        referenceId,
        retries: retries + 1
      })
    }
  }

  const handlePurchaseOrderError = async ({
    paymentGateway,
    orderId,
    referenceId
  }) => {
    setPaymentState((prevState) => ({
      ...prevState,
      isPaying: false,
      hasConfirmedPaymentMethod: false,
      paymentError: 'Ocurrió un error al crear la orden de compra, tu pago será reembolsado',
      isSavingCard: false,
      forceNewPayment: true,
      selectedPaymentOption: ''
    }))
    resetCardData()
    setShowFinalLoader(false)
    return await refundPaymentOrder({
      paymentGateway,
      orderId,
      referenceId
    })
  }

  return {
    markShopifyOrderAsPaid,
    handlePurchaseOrderSuccess,
    retryPurchaseOrder,
    createPurchaseOrder
  }
}
